/**
 * Scope Variable Collector — computes the ScopeVariable[] available
 * at a given definition's position in the document AST.
 *
 * Supports:
 *   - `::for` loop variables ($each / $each_index)
 *   - `::use` template params ($ref{input/xxx})  — Phase B
 *   - Content block nesting (outer for-vars visible inside content body) — Phase C
 *   - Content template inputs visible to definitions inside content bodies
 */

import type {
    ContentDefinition,
    DocumentBlock,
    ForBlock,
    ForDefinition,
    ForOutputMetadata,
    ParsedDocument,
    PluginDefinition,
    TemplateInputSchema,
    UseBinding,
} from "@magic-md/core";
import {
    applyTemplateInputRefsToValue,
    extractTemplateParams,
    parseMagicMd,
    ReferenceResolver,
} from "@magic-md/core";
import { t as translate } from "@magic-md/i18n";
import type { ScopeVariable } from "@magic-md/plugin-sdk";
import { collectForScopedUnits } from "./for-scope-units";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

const resolver = new ReferenceResolver();

/** Extra options for collectScopeVariables (all optional for backward compatibility) */
export interface CollectScopeOptions {
    /** ::use bindings（ParsedDocument.uses） */
    uses?: UseBinding[];
    /** Content definitions（ParsedDocument.contentDefinitions） */
    contentDefinitions?: Map<string, ContentDefinition>;
}

// ── Public API ──

export function collectTemplateInputScopeVariables(
    schema: TemplateInputSchema | undefined,
    params: readonly string[] | undefined,
    values?: Record<string, unknown>,
): ScopeVariable[] {
    const properties = schema?.properties ?? {};
    const ordered = schema?.order?.filter((name) => properties[name]) ?? [];
    const names = [
        ...ordered,
        ...Object.keys(properties).filter((name) => !ordered.includes(name)),
        ...(params ?? []).filter((name) => !properties[name]),
    ];
    const seen = new Set<string>();
    const result: ScopeVariable[] = [];
    for (const name of names) {
        if (!/^\w[\w-]*$/.test(name) || seen.has(name)) continue;
        seen.add(name);
        const property = properties[name];
        const sampleValue = values && Object.prototype.hasOwnProperty.call(values, name)
            ? values[name]
            : property && Object.prototype.hasOwnProperty.call(property, "default")
                ? property.default
                : undefined;
        result.push({
            displayName: `input/${name}`,
            refTemplate: `$ref{input/${name}}`,
            origin: { type: "custom", directiveName: "template-input" },
            description: property?.title || property?.description || tr("scopeVariables.templateInput"),
            sampleValue,
            browsable: typeof sampleValue === "object" && sampleValue !== null,
            color: "blue",
        });
    }
    return result;
}

/**
 * Collect all scope variables visible at the position of the given definition.
 *
 * @param defId          - The definition ID whose scope is being inspected
 * @param blocks         - The top-level block tree (ParsedDocument.blocks)
 * @param forDefinitions - For-loop definitions (for resolving output/item count)
 * @param definitions    - All top-level definitions (for resolving $ref{} sample values)
 * @param options        - Optional: uses, contentDefinitions for Phase B/C support
 */
export function collectScopeVariables(
    defId: string,
    blocks: DocumentBlock[],
    forDefinitions: Map<string, ForDefinition> | undefined,
    definitions: Map<string, PluginDefinition>,
    options?: CollectScopeOptions,
): ScopeVariable[] {
    const result: ScopeVariable[] = [];

    // ① Collect enclosing ::for blocks (outer → inner order)
    const forChain = findEnclosingForBlocks(defId, blocks);
    for (const forBlock of forChain) {
        const forDef = forDefinitions?.get(forBlock.id);
        const sampleItem = resolveSampleItem(forBlock.of, definitions, forDef);
        const itemCount = resolveItemCount(forBlock.of, definitions, forDef);
        result.push(
            ...forScopeVariables(forBlock.each, forBlock.of, sampleItem, itemCount),
        );
    }

    const knownForIds = new Set(forChain.map((block) => block.id));
    for (const forDef of findEnclosingForDefinitions(defId, forDefinitions)) {
        if (knownForIds.has(forDef.id)) continue;
        result.push(
            ...forScopeVariables(
                forDef.each,
                forDef.of,
                resolveSampleItem(forDef.of, definitions, forDef),
                resolveItemCount(forDef.of, definitions, forDef),
            ),
        );
    }

    // ② Phase B: collect ::use template params ($ref{input/xxx})
    if (options?.uses && options?.contentDefinitions) {
        result.push(
            ...collectUseTemplateParams(defId, options.uses, options.contentDefinitions),
        );
    }

    // ③ Content template inputs: definitions inside content templates can see
    // the parent content's declared/used input namespace.
    if (options?.contentDefinitions) {
        result.push(
            ...collectContentTemplateInputScope(defId, options.contentDefinitions),
        );
    }

    // ④ Phase C: collect outer scope for content-nested definitions
    if (options?.contentDefinitions && forDefinitions) {
        result.push(
            ...collectContentNestingScope(defId, blocks, forDefinitions, definitions, options.contentDefinitions),
        );
    }

    return uniqueScopeVariables(result);
}

// ── For Loop Variable Helpers ──

/**
 * Generate 2 ScopeVariables for a ::for block: $each (iterable item) + $each_index
 */
function forScopeVariables(
    each: string,
    source: string,
    sampleItem: unknown,
    itemCount?: number,
): ScopeVariable[] {
    return [
        {
            displayName: `$${each}`,
            refTemplate: `$ref{$${each}}`,
            origin: { type: "for", variableName: each, source, itemCount },
            description: tr("scopeVariables.loopVariable", { each, source }),
            sampleValue: sampleItem,
            browsable: typeof sampleItem === "object" && sampleItem !== null,
            color: "purple",
        },
        {
            displayName: `$${each}_index`,
            refTemplate: `$ref{$${each}_index}`,
            origin: { type: "for", variableName: each, source, itemCount },
            description: tr("scopeVariables.loopIndex", { each }),
            sampleValue: 0,
            browsable: false,
            color: "purple",
        },
    ];
}

// ── AST Traversal ──

/**
 * Find all for-blocks that enclose a given definition ID, from outermost to innermost.
 */
export function findEnclosingForBlocks(
    defId: string,
    blocks: DocumentBlock[],
): ForBlock[] {
    const chain: ForBlock[] = [];
    findForBlocksRecursive(defId, blocks, chain);
    return chain;
}

export function findEnclosingForDefinitions(
    defId: string,
    forDefinitions: Map<string, ForDefinition> | undefined,
): ForDefinition[] {
    if (!forDefinitions) return [];

    const visit = (
        forDef: ForDefinition,
        chain: ForDefinition[],
    ): ForDefinition[] | null => {
        const currentChain = [...chain, forDef];
        if (
            forDef.id === defId ||
            forDef.innerScope.definitions.has(defId) ||
            forDef.innerScope.contentDefinitions.has(defId) ||
            (forDef.innerScope.uses ?? []).some((use) => use.id === defId)
        ) {
            return currentChain;
        }

        for (const nested of forDef.innerScope.forDefinitions.values()) {
            const found = visit(nested, currentChain);
            if (found) return found;
        }

        return null;
    };

    for (const forDef of forDefinitions.values()) {
        const found = visit(forDef, []);
        if (found) return found;
    }
    return [];
}

/**
 * Recursively search for the definition in the block tree.
 * When found inside a for-block, add that for-block to the chain.
 * Returns true if the definition was found (at any depth).
 */
function findForBlocksRecursive(
    defId: string,
    blocks: DocumentBlock[],
    chain: ForBlock[],
): boolean {
    for (const block of blocks) {
        // Direct definition match
        if (block.type === "definition" && block.id === defId) {
            return true;
        }

        // Render block referencing this definition
        if (
            (block.type === "inline-render" || block.type === "block-render") &&
            block.definitionId === defId
        ) {
            return true;
        }

        // For-block: check if definition is inside children
        if (block.type === "for-block") {
            // Check children — if found, this for-block encloses the definition
            const innerChain: ForBlock[] = [];
            if (findForBlocksRecursive(defId, block.children, innerChain)) {
                // This for-block is an enclosing scope
                chain.push(block);
                // Also add any nested for-blocks found
                chain.push(...innerChain);
                return true;
            }
        }
    }
    return false;
}

// ── Sample Value Resolution ──

/**
 * Resolve the `of` expression to get a sample item (first element of the source array).
 * Uses ReferenceResolver to resolve $ref{} references.
 */
function resolveSampleItem(
    ofExpr: string,
    definitions: Map<string, PluginDefinition>,
    forDef?: ForDefinition,
): unknown {
    // First try: if forDef has executed output, use first iteration's item
    if (forDef?.output?.iterations?.[0]?.item !== undefined) {
        return forDef.output.iterations[0].item;
    }

    // Second: resolve the $ref{} expression to get the source array
    const sourceArray = resolveOfArray(ofExpr, definitions);
    if (sourceArray.length > 0) {
        return sourceArray[0];
    }

    return undefined;
}

/**
 * Resolve the `of` expression to get the item count.
 */
function resolveItemCount(
    ofExpr: string,
    definitions: Map<string, PluginDefinition>,
    forDef?: ForDefinition,
): number | undefined {
    // Use forDef output meta if available
    if (forDef?.output?.__metadata !== undefined) {
        return (forDef.output.__metadata as ForOutputMetadata).total;
    }

    // Resolve from source array
    const sourceArray = resolveOfArray(ofExpr, definitions);
    return sourceArray.length > 0 ? sourceArray.length : undefined;
}

/**
 * Resolve an `of` expression (e.g. `$ref{repos.output.items}`) to its source array.
 */
function resolveOfArray(
    ofExpr: string,
    definitions: Map<string, PluginDefinition>,
): unknown[] {
    try {
        const resolved = resolver.resolveConfig({ __source: ofExpr }, (id) =>
            definitions.get(id),
        );
        const source = resolved.__source;
        if (Array.isArray(source)) return source;
    } catch {
        // Resolution failed — return empty
    }
    return [];
}

function uniqueScopeVariables(values: ScopeVariable[]): ScopeVariable[] {
    const seen = new Set<string>();
    const result: ScopeVariable[] = [];
    for (const value of values) {
        if (seen.has(value.displayName)) continue;
        seen.add(value.displayName);
        result.push(value);
    }
    return result;
}

// ── For-inner / Content-inner Definition Helpers ──

/**
 * Find the parent for-block ID that directly contains the given definition.
 * Returns `undefined` if the definition is not inside any for-block.
 */
export function findParentForBlockId(
    defId: string,
    forDefinitions: Map<string, ForDefinition> | undefined,
): string | undefined {
    if (!forDefinitions) return undefined;
    for (const [forId, forDef] of forDefinitions) {
        if (forDef.innerScope.definitions.has(defId)) {
            return forId;
        }
    }
    return undefined;
}

/**
 * Find the parent content-block ID that contains a scoped executable unit in
 * its body. Callers decide whether to run the local unit or the parent content.
 */
export function findParentContentBlockId(
    defId: string,
    contentDefinitions: Map<string, ContentDefinition> | undefined,
): string | undefined {
    return findParentContentBlockIds(defId, contentDefinitions)[0];
}

function findParentContentBlockIds(
    defId: string,
    contentDefinitions: Map<string, ContentDefinition> | undefined,
): string[] {
    if (!contentDefinitions) return [];
    // Escape regex special chars in defId for safe matching
    const escaped = defId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`::(?:define|use|content|for)\\[${escaped}\\]`);
    const parentIds: string[] = [];
    for (const [contentId, contentDef] of contentDefinitions) {
        if (re.test(contentDef.body)) {
            parentIds.push(contentId);
        }
    }
    return parentIds;
}

export function collectContentTemplateInputScope(
    defId: string,
    contentDefinitions: Map<string, ContentDefinition> | undefined,
): ScopeVariable[] {
    const parentContentIds = findParentContentBlockIds(defId, contentDefinitions);
    if (!contentDefinitions || parentContentIds.length === 0) return [];

    const result: ScopeVariable[] = [];
    for (const contentId of parentContentIds) {
        const contentDef = contentDefinitions.get(contentId);
        if (!contentDef?.template) continue;
        result.push(
            ...collectTemplateInputScopeVariables(
                contentDef.inputSchema,
                contentDef.params,
            ),
        );
    }
    return uniqueScopeVariables(result);
}

/**
 * Build a merged definitions map that includes top-level, for-inner, and
 * content-inner definitions (same merge logic as PaneContext).
 */
export function getMergedDefinitions(
    parsedDocument: ParsedDocument | null | undefined,
): Map<string, PluginDefinition> {
    const defs = new Map(
        parsedDocument?.definitions ?? new Map<string, PluginDefinition>(),
    );
    if (parsedDocument?.forDefinitions) {
        for (const forDef of parsedDocument.forDefinitions.values()) {
            for (const innerDef of forDef.innerScope.definitions.values()) {
                if (!defs.has(innerDef.id)) {
                    defs.set(innerDef.id, innerDef);
                }
            }
        }
        const forScopedUnits = collectForScopedUnits(parsedDocument.forDefinitions);
        for (const [id, scoped] of forScopedUnits.definitions) {
            if (!defs.has(id)) {
                defs.set(id, scoped.definition);
            }
        }
        for (const use of forScopedUnits.uses.map((entry) => entry.use)) {
            if (defs.has(use.id)) continue;
            const source = defs.get(use.source);
            if (!source) continue;
            const templateConfig = source.template
                ? applyTemplateInputRefsToValue(
                    source.config,
                    use.with ?? {},
                ) as Record<string, unknown>
                : source.config;
            const config = use.patch ? { ...templateConfig, ...use.patch } : { ...templateConfig };
            const params = extractTemplateParams(config);
            defs.set(use.id, {
                ...source,
                id: use.id,
                config,
                template: use.template === true || params.length > 0,
                params: params.length > 0 ? params : undefined,
                output: use.output?.nodeOutput,
            });
        }
    }
    if (parsedDocument?.contentDefinitions) {
        const queue = [...parsedDocument.contentDefinitions.values()];
        const seenContentIds = new Set(parsedDocument.contentDefinitions.keys());
        for (let i = 0; i < queue.length; i++) {
            const nested = parseMagicMd(queue[i].body);
            for (const [id, def] of nested.definitions) {
                if (!defs.has(id)) {
                    defs.set(id, def);
                }
            }
            for (const [id, contentDef] of nested.contentDefinitions ?? new Map<string, ContentDefinition>()) {
                if (!seenContentIds.has(id)) {
                    seenContentIds.add(id);
                    queue.push(contentDef);
                }
            }
        }
    }
    return defs;
}

// ── Phase B: ::use Template Params ──

/**
 * If defId was created by `::use` from a content template,
 * inject the template's $ref{input/xxx} parameters as scope variables.
 *
 * Flow: defId → matching UseBinding → source content def → params[]
 */
export function collectUseTemplateParams(
    defId: string,
    uses: UseBinding[],
    contentDefinitions: Map<string, ContentDefinition>,
): ScopeVariable[] {
    // Find the ::use binding that created this definition
    const useBinding = uses.find((u) => u.id === defId);
    if (!useBinding) return [];

    // Resolve source: could be content or plugin definition
    const sourceContent = contentDefinitions.get(useBinding.source);
    if (!sourceContent?.template || !sourceContent.params?.length) return [];

    // Generate scope variables for each template parameter
    return sourceContent.params.map((param): ScopeVariable => ({
        displayName: `input/${param}`,
        refTemplate: `$ref{input/${param}}`,
        origin: { type: "custom", directiveName: "use-template" },
        description: tr("scopeVariables.templateParamFromWith"),
        sampleValue: useBinding.with?.[param],
        browsable: typeof useBinding.with?.[param] === "object" && useBinding.with?.[param] !== null,
        color: "blue",
    }));
}

// ── Phase C: Content Block Nesting Scope ──

/**
 * If defId is inside a content block, and that content block is
 * itself inside a ::for loop, the definition should see the ::for
 * loop variables from the outer scope.
 *
 * Also handles nested for-blocks inside content blocks.
 */
export function collectContentNestingScope(
    defId: string,
    blocks: DocumentBlock[],
    forDefinitions: Map<string, ForDefinition>,
    definitions: Map<string, PluginDefinition>,
    contentDefinitions: Map<string, ContentDefinition>,
): ScopeVariable[] {
    // Check if defId lives inside any content block's innerScope
    // Content blocks' inner definitions live in forDefinitions' innerScope
    // or in content definitions' output structure. But at the block-tree level,
    // content blocks appear as top-level or nested blocks.

    // Strategy: find a content block that contains this defId by scanning
    // the block tree for content-render blocks whose source content definition
    // has inner definitions containing defId.
    const parentContentId = findParentContentBlockId(defId, contentDefinitions);
    if (!parentContentId) return [];

    // Now find for-blocks that enclose this content block's render location
    const enclosingForBlocks = findEnclosingForBlocks(parentContentId, blocks);
    if (enclosingForBlocks.length === 0) return [];

    // Don't duplicate variables already collected by step ①
    // (step ① already collected for-vars for defId itself; step ③ adds
    // for-vars from the content block's outer scope that defId doesn't see)
    const existingForIds = new Set(findEnclosingForBlocks(defId, blocks).map((b) => b.id));

    const result: ScopeVariable[] = [];
    for (const forBlock of enclosingForBlocks) {
        if (existingForIds.has(forBlock.id)) continue; // already collected
        const forDef = forDefinitions.get(forBlock.id);
        const sampleItem = resolveSampleItem(forBlock.of, definitions, forDef);
        const itemCount = resolveItemCount(forBlock.of, definitions, forDef);
        result.push(
            ...forScopeVariables(forBlock.each, forBlock.of, sampleItem, itemCount),
        );
    }
    return result;
}
